import type {
  ObjectiveGroup,
  ObjectiveWithProgress
} from '@/types/objective'

/**
 * Uma **meta**: o que o participante persegue.
 *
 * `single` é um objetivo solto, como sempre foi. `ladder` é uma escada de
 * marcos — N objetivos que o produtor agrupou. Os dois casos convivem na mesma
 * lista, e o denominador das métricas conta metas, não degraus.
 */
export type Goal =
  | { kind: 'single'; id: string; objective: ObjectiveWithProgress; position: number }
  | {
      kind: 'ladder'
      id: string
      group: ObjectiveGroup
      milestones: ObjectiveWithProgress[]
      position: number
    }

/** Quantidade alvo do marco — a condição é única por homogeneidade da escada. */
export const milestoneTarget = (objective: ObjectiveWithProgress): number =>
  objective.objective_conditions?.[0]?.quantity ?? 0

/**
 * Contagem cumulativa do participante naquele recurso.
 *
 * Todos os marcos reportam a mesma contagem — `ConditionCheckerService` devolve
 * o `count` cru, sem clamp — então basta ler a maior. Com `conditions_progress`
 * vazio (nunca iniciado) a contagem é zero, mesmo tratamento que
 * `getProgressData` já dá ao objetivo solto.
 */
export const ladderCurrentCount = (milestones: ObjectiveWithProgress[]): number =>
  milestones.reduce((max, milestone) => {
    const count = milestone.conditions_progress?.[0]?.count ?? 0
    return Math.max(max, count)
  }, 0)

/**
 * Monta as metas a partir da lista crua de objetivos.
 *
 * O agrupamento vem **exclusivamente** de `objective_group_id`. O cliente nunca
 * infere escada por similaridade de condição: um produtor que cria "Visite 3
 * expositores" como aquecimento e "Visite os 75" como épico quer duas metas
 * separadas, e a heurística destruiria essa intenção.
 *
 * Grupo com um único marco é rebaixado a `single` aqui, na construção — assim a
 * degradação para o card comum é consequência do modelo, e não um `if`
 * espalhado por cada componente.
 */
export function buildGoals(objectives: ObjectiveWithProgress[] = []): Goal[] {
  const ladders = new Map<number, ObjectiveWithProgress[]>()
  const groups = new Map<number, ObjectiveGroup>()
  const goals: Goal[] = []

  objectives.forEach(objective => {
    const groupId = objective.objective_group_id

    if (!groupId) {
      goals.push({
        kind: 'single',
        id: `objective-${objective.id}`,
        objective,
        position: objective.position ?? 0
      })
      return
    }

    if (!ladders.has(groupId)) {
      ladders.set(groupId, [])
      groups.set(
        groupId,
        objective.objective_group ?? { id: groupId, name: 'Meta', description: null }
      )
    }

    ladders.get(groupId)!.push(objective)
  })

  ladders.forEach((milestones, groupId) => {
    // Ordem da escada é a da quantidade, nunca a de `position`.
    const ordered = [...milestones].sort(
      (a, b) => milestoneTarget(a) - milestoneTarget(b)
    )

    const position = Math.min(...ordered.map(milestone => milestone.position ?? 0))

    if (ordered.length === 1) {
      goals.push({
        kind: 'single',
        id: `objective-${ordered[0].id}`,
        objective: ordered[0],
        position
      })
      return
    }

    goals.push({
      kind: 'ladder',
      id: `group-${groupId}`,
      group: groups.get(groupId)!,
      milestones: ordered,
      position
    })
  })

  return goals.sort((a, b) => a.position - b.position)
}

export type LadderState = {
  currentCount: number
  achievedCount: number
  totalMilestones: number
  /** Alvo total da escada — quantidade do último marco. */
  totalTarget: number
  /** Quantidades de cada marco, em ordem ascendente. */
  checkpointTargets: number[]
  nextMilestone: ObjectiveWithProgress | null
  nextTarget: number | null
  nextReward: number | null
  remaining: number | null
  /** Avanço dentro do marco corrente, de 0 a 1. */
  segmentFill: number
  completed: boolean
  totalCoinReward: number
  /** Recompensa por marco quando todas são iguais; `null` quando divergem. */
  uniformCoinReward: number | null
}

/**
 * Estado de leitura de uma escada — tudo que o card precisa dizer.
 *
 * A informação acionável é sempre a distância até o **próximo** marco, nunca o
 * percentual da escada inteira: "7 de 75" e "7 de 10" no mesmo card seriam duas
 * metas concorrentes, e o participante não saberia qual perseguir.
 */
export function getLadderState(milestones: ObjectiveWithProgress[]): LadderState {
  const ordered = [...milestones].sort(
    (a, b) => milestoneTarget(a) - milestoneTarget(b)
  )

  const currentCount = ladderCurrentCount(ordered)
  const achieved = ordered.filter(milestone => currentCount >= milestoneTarget(milestone))
  const achievedCount = achieved.length
  const nextMilestone = ordered[achievedCount] ?? null

  const previousTarget = achievedCount > 0 ? milestoneTarget(ordered[achievedCount - 1]) : 0
  const nextTarget = nextMilestone ? milestoneTarget(nextMilestone) : null

  const span = nextTarget !== null ? nextTarget - previousTarget : 0
  const segmentFill =
    span > 0 ? Math.min(1, Math.max(0, (currentCount - previousTarget) / span)) : 0

  const rewards = ordered.map(milestone => milestone.coin_reward ?? 0)
  const allEqual = rewards.every(reward => reward === rewards[0])
  const checkpointTargets = ordered.map(milestoneTarget)
  const totalTarget = checkpointTargets[checkpointTargets.length - 1] ?? 0

  return {
    currentCount,
    achievedCount,
    totalMilestones: ordered.length,
    totalTarget,
    checkpointTargets,
    nextMilestone,
    nextTarget,
    nextReward: nextMilestone ? nextMilestone.coin_reward ?? 0 : null,
    remaining: nextTarget !== null ? Math.max(0, nextTarget - currentCount) : null,
    segmentFill,
    completed: nextMilestone === null,
    totalCoinReward: rewards.reduce((sum, reward) => sum + reward, 0),
    uniformCoinReward: rewards.length > 0 && allEqual ? rewards[0] : null
  }
}

/** Uma meta está concluída? */
export function isGoalCompleted(goal: Goal): boolean {
  if (goal.kind === 'single') return Boolean(goal.objective.completed)
  return getLadderState(goal.milestones).completed
}

/** A meta já foi começada? */
export function isGoalStarted(goal: Goal): boolean {
  if (goal.kind === 'single') {
    const progress = goal.objective.conditions_progress ?? []
    return progress.some(entry => entry.count > 0)
  }
  return getLadderState(goal.milestones).currentCount > 0
}

/** Soma de moedas que a meta pode render. */
export function goalCoinReward(goal: Goal): number {
  if (goal.kind === 'single') return goal.objective.coin_reward ?? 0
  return getLadderState(goal.milestones).totalCoinReward
}
